import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Flex, HStack, Input, Text, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { autorun } from "mobx";
import { useParams } from "react-router-dom";
import { getDeck, PresentationModel, unlockPresentation, whyDeck } from "@lubbezposrednio-frontend/core";
import {
  DeckEngine,
  NotesOverlay,
  OverviewGrid,
  PresenterLayout,
  PrintDeck,
  ProgressBar,
  SlideNav
} from "@lubbezposrednio-frontend/commons";
import { DeviceCluster } from "./PresentationScenes";

// Sceny korzystające z prawdziwych komponentów aplikacji (poza commons).
const CUSTOM_SCENES: Record<string, ReactNode> = {
  "device-trio": <DeviceCluster />
};

// Druk/PDF: własny rozmiar kartki = sam slajd (jak custom document size w InDesign).
// Kartka 13,333 × 7,5 cala = standard prezentacji 16:9 (PowerPoint widescreen) i dokładnie
// 1280×720 px przy 96 dpi → kanwa wypełnia całą stronę 1:1, bez letterboxa. 1 slajd = 1 kartka.
//
// Dwa krytyczne szczegóły dla Chrome:
//  • print-color-adjust:exact — wymusza DRUK TŁA (gradienty/kolory). Bez tego Chrome (domyślnie
//    „Background graphics" wyłączone) drukuje slajdy bez teł — „posypane".
//  • na ekranie kontener jest poza ekranem (nie display:none), żeby sceny/animacje framer-motion
//    zdążyły się wyrenderować i ustawić, zanim trafią do druku.
const PRINT_CSS = `
@media screen {
  .print-deck { position: fixed; left: -200vw; top: 0; width: 1280px; pointer-events: none; }
}
@media print {
  @page { size: 13.3333in 7.5in; margin: 0; }
  html, body { background: #fff !important; margin: 0 !important; }
  .prez-screen { display: none !important; }
  .print-deck { position: static !important; left: auto !important; }
  .print-deck, .print-deck * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .print-slide {
    width: 13.3333in; height: 7.5in;
    overflow: hidden;
    page-break-after: always; break-after: page; break-inside: avoid;
  }
  .print-slide:last-child { page-break-after: auto; break-after: auto; }
  .print-stage { width: 1280px; height: 720px; transform: none; }
}
`;

const UNLOCK_KEY = "prez_unlocked";
const isUnlocked = () => {
  try {
    return localStorage.getItem(UNLOCK_KEY) === "1";
  } catch {
    return false;
  }
};

interface Props {
  presenter?: boolean;
}

// Widok-glue: spina model (MVVM) z prezentacyjnymi komponentami commons. Klawiatura, pełny ekran,
// synchronizacja okna prezentera (BroadcastChannel), tryby present/overview/notes.
// Notatki i tryb prezentera chronione hasłem z backendu (Setting.presentation_password).
export const PresentationView: FC<Props> = observer(({ presenter = false }) => {
  const { deckSlug, index } = useParams();
  const deck = getDeck(deckSlug) ?? whyDeck;
  const parsed = index ? parseInt(index, 10) - 1 : 0;
  const [model] = useState(() => new PresentationModel(deck, isNaN(parsed) ? 0 : parsed));
  const rootRef = useRef<HTMLDivElement>(null);
  const suppress = useRef(false);

  const [unlocked, setUnlocked] = useState(isUnlocked);
  const [pwOpen, setPwOpen] = useState(false);
  const pendingRef = useRef<null | (() => void)>(null);

  const doUnlock = async (password: string): Promise<boolean> => {
    try {
      const res = await unlockPresentation(password);
      if (res.data?.unlocked) {
        localStorage.setItem(UNLOCK_KEY, "1");
        setUnlocked(true);
        return true;
      }
    } catch {
      // 401 / błąd sieci → traktujemy jak błędne hasło
    }
    return false;
  };

  // Wykonaj akcję tylko po odblokowaniu; inaczej pokaż okno hasła (świeży odczyt z localStorage).
  const requireUnlock = (action: () => void) => {
    if (isUnlocked()) {
      action();
      return;
    }
    pendingRef.current = action;
    setPwOpen(true);
  };

  const askNotes = () => {
    if (model.mode === "notes") {
      model.present();
      return;
    }
    requireUnlock(() => model.setMode("notes"));
  };

  const toggleFullscreen = () => {
    const el = rootRef.current;
    if (!document.fullscreenElement && el?.requestFullscreen) el.requestFullscreen().catch(() => undefined);
    else if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
  };
  const openPresenter = () => {
    requireUnlock(() => window.open(`/prezentacja/${deck.slug}/prezenter`, "prezenter", "width=1100,height=720"));
  };

  // Pobierz PDF: drukuje statyczną wersję wszystkich slajdów (każdy = jedna strona 16:9).
  // Publiczne — slajdy są jawne, więc bez hasła. Tytuł dokumentu = domyślna nazwa pliku PDF.
  const downloadPdf = () => {
    const prevTitle = document.title;
    document.title = `${deck.title} — lubbezpośrednio.pl`;
    const restore = () => {
      document.title = prevTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  };

  // zegar prezentera
  useEffect(() => {
    const t = window.setInterval(() => model.tick(), 1000);
    return () => window.clearInterval(t);
  }, [model]);

  // synchronizacja okien (rzutnik ↔ prezenter)
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel(`prez-${deck.slug}`);
    ch.onmessage = (ev: MessageEvent) => {
      if (ev.data?.type === "goto" && typeof ev.data.index === "number") {
        suppress.current = true;
        model.setIndex(ev.data.index);
        suppress.current = false;
      }
    };
    const dispose = autorun(() => {
      const i = model.currentIndex;
      if (!suppress.current) ch.postMessage({ type: "goto", index: i });
    });
    return () => {
      dispose();
      ch.close();
    };
  }, [model, deck.slug]);

  // klawiatura
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
        case " ":
        case "PageDown":
          e.preventDefault();
          model.goNext.execute().catch(() => undefined);
          break;
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          model.goPrev.execute().catch(() => undefined);
          break;
        case "f":
        case "F":
          toggleFullscreen();
          break;
        case "o":
        case "O":
          model.toggleOverview();
          break;
        case "p":
        case "P":
          askNotes();
          break;
        case "Escape":
          model.present();
          break;
        default:
          if (/^[1-9]$/.test(e.key)) model.goTo(parseInt(e.key, 10) - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [model]);

  // stan pełnego ekranu
  useEffect(() => {
    const onFs = () => model.setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [model]);

  // Tryb prezentera — wymaga odblokowania (osobne okno).
  if (presenter) {
    if (!unlocked) {
      return (
        <Flex position="fixed" inset={0} bg="#0F0A1F" align="center" justify="center" zIndex={3000}>
          <UnlockForm onUnlock={doUnlock} />
        </Flex>
      );
    }
    return (
      <PresenterLayout
        current={model.current}
        next={model.next}
        index={model.currentIndex}
        total={model.total}
        elapsedSec={model.elapsedSec}
        progress={model.progress}
        canPrev={model.currentIndex > 0}
        canNext={model.currentIndex < model.total - 1}
        onPrev={() => model.goPrev.execute().catch(() => undefined)}
        onNext={() => model.goNext.execute().catch(() => undefined)}
      />
    );
  }

  return (
    <>
      {/* Style druku: na ekranie ukryty kontener PDF; w druku ukryta apka, każdy slajd = strona 16:9. */}
      <style>{PRINT_CSS}</style>

    <Box ref={rootRef} className="prez-screen" position="fixed" inset={0} bg="#000" zIndex={3000}>
      {model.mode === "overview" ? (
        <OverviewGrid
          slides={deck.slides}
          currentIndex={model.currentIndex}
          onSelect={(i) => {
            model.goTo(i);
            model.present();
          }}
        />
      ) : (
        <>
          <DeckEngine slide={model.current} direction={model.direction} reduced={model.reducedMotion} customScenes={CUSTOM_SCENES} />
          {model.mode === "notes" && (
            <NotesOverlay notes={model.current.notes} next={model.next} elapsedSec={model.elapsedSec} />
          )}
          <ProgressBar progress={model.progress} />
          <SlideNav
            index={model.currentIndex}
            total={model.total}
            canPrev={model.currentIndex > 0}
            canNext={model.currentIndex < model.total - 1}
            onPrev={() => model.goPrev.execute().catch(() => undefined)}
            onNext={() => model.goNext.execute().catch(() => undefined)}
            onOverview={() => model.toggleOverview()}
          />
          <HStack position="absolute" top="14px" right="16px" spacing="8px">
            <ToolBtn label="Pobierz PDF" onClick={downloadPdf} />
            <ToolBtn label="Prezenter" onClick={openPresenter} />
            <ToolBtn label="Notatki (P)" onClick={askNotes} />
            <ToolBtn label="Pełny ekran (F)" onClick={toggleFullscreen} />
          </HStack>
        </>
      )}

      {pwOpen && (
        <Flex position="absolute" inset={0} bg="rgba(0,0,0,0.72)" align="center" justify="center" zIndex={60}>
          <UnlockForm
            onUnlock={async (pw) => {
              const ok = await doUnlock(pw);
              if (ok) {
                setPwOpen(false);
                pendingRef.current?.();
                pendingRef.current = null;
              }
              return ok;
            }}
            onCancel={() => {
              setPwOpen(false);
              pendingRef.current = null;
            }}
          />
        </Flex>
      )}
    </Box>

      {/* Kontener do druku/PDF — ukryty na ekranie, widoczny tylko podczas drukowania. */}
      <PrintDeck slides={deck.slides} customScenes={CUSTOM_SCENES} />
    </>
  );
});

const UnlockForm: FC<{ onUnlock: (pw: string) => Promise<boolean>; onCancel?: () => void }> = ({ onUnlock, onCancel }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!pw || loading) return;
    setLoading(true);
    setErr(false);
    const ok = await onUnlock(pw);
    setLoading(false);
    if (!ok) {
      setErr(true);
      setPw("");
    }
  };
  return (
    <VStack spacing="14px" bg="#170E2E" border="1px solid rgba(255,255,255,0.14)" borderRadius="20px" p="30px" w="360px" maxW="90vw" boxShadow="0 30px 80px rgba(0,0,0,0.5)">
      <Text color="#fff" fontSize="20px" fontWeight="900">
        Notatki i tryb prezentera
      </Text>
      <Text color="rgba(255,255,255,0.6)" fontSize="13px" textAlign="center" lineHeight="1.5">
        Slajdy są publiczne. Podaj hasło prowadzącego, aby odblokować notatki i widok prezentera.
      </Text>
      <Input
        type="password"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="Hasło"
        autoFocus
        bg="rgba(255,255,255,0.08)"
        border="1px solid"
        borderColor={err ? "#E51F44" : "rgba(255,255,255,0.18)"}
        color="#fff"
        _placeholder={{ color: "rgba(255,255,255,0.4)" }}
        _focus={{ borderColor: "#E51F44", boxShadow: "none" }}
      />
      {err && (
        <Text color="#E51F44" fontSize="12px" fontWeight="600">
          Błędne hasło.
        </Text>
      )}
      <HStack w="100%" spacing="10px" pt="2px">
        {onCancel && (
          <Box as="button" flex="1" onClick={onCancel} py="11px" borderRadius="12px" bg="rgba(255,255,255,0.1)" color="#fff" fontWeight="700" fontSize="14px">
            Anuluj
          </Box>
        )}
        <Box as="button" flex="1" onClick={submit} py="11px" borderRadius="12px" bg="#E51F44" color="#fff" fontWeight="800" fontSize="14px" opacity={loading ? 0.6 : 1}>
          {loading ? "Sprawdzam…" : "Odblokuj"}
        </Box>
      </HStack>
    </VStack>
  );
};

const ToolBtn: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Box
    as="button"
    onClick={onClick}
    bg="rgba(255,255,255,0.9)"
    color="#430098"
    fontSize="13px"
    fontWeight="700"
    px="12px"
    py="7px"
    borderRadius="full"
    boxShadow="0 4px 14px rgba(0,0,0,0.12)"
  >
    {label}
  </Box>
);

export default PresentationView;
